import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Stack,
  Container,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ShieldOutlined,
  LockOutlined,
} from "@mui/icons-material";
import logo from "../../assets/logo.png";
import { UserContext } from "../../features/UserPanel/userContext/UserContext";
import { registrarSalesEvent } from "../../utils/salesEvents.js";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { setUser } = useContext(UserContext); // Extraí a função setUser
  const [showPassword, setShowPassword] = useState(false);
  

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      alert("Por favor, informe o email e a senha.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("role", data.role);

        // Aqui armazenamos o usuário no contexto.
        setUser({
          id: data.userId,
          email: email,
          role: data.role,
          isPaid: data.isPaid,
          accessHold: data.accessHold || null,
        });

        if (data.role !== "admin" && data.userId) {
          registrarSalesEvent(data.userId, "user_login", {
            source: "login",
            metadata: { path: window.location.pathname },
          });
        }

        if (data.role === "admin") {
          window.location.href = "/admin";
        } else {
          window.location.href = "/painel";
        }
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Erro ao fazer login:", error);
      alert("Erro no servidor. Tente novamente.");
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: { xs: 2, md: 4 },
        background:
          "radial-gradient(circle at 15% 20%, rgba(52,84,140,0.35) 0%, rgba(7,12,20,1) 48%), radial-gradient(circle at 85% 10%, rgba(33,128,103,0.22) 0%, rgba(7,12,20,0) 45%), #070C14",
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: { xs: 420, md: 980 },
          minHeight: { xs: "auto", md: 560 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" },
          overflow: "hidden",
          borderRadius: 4,
          border: "1px solid rgba(255,255,255,0.12)",
          bgcolor: "rgba(10,16,24,0.9)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.42)",
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            p: { xs: 3, md: 5 },
            background:
              "linear-gradient(145deg, rgba(20,31,46,0.98) 0%, rgba(13,20,33,0.96) 100%)",
            borderRight: { md: "1px solid rgba(255,255,255,0.08)" },
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Box
              component="img"
              src={logo}
              alt="Logo Future Trade"
              sx={{ width: 74, height: 74, borderRadius: "50%", mb: 2.5 }}
            />
            <Typography sx={{ fontSize: { xs: "1.7rem", md: "2rem" }, fontWeight: 900, color: "#F3F8FF", mb: 1 }}>
              Future Trade - Painel
            </Typography>
            <Typography sx={{ color: "rgba(231,240,250,0.74)", maxWidth: 380 }}>
              Acesse seus indicadores, arquivos e acompanhamento com autenticação segura.
            </Typography>
          </Box>

          <Stack spacing={1.4}>
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgba(231,240,250,0.88)" }}>
              <ShieldOutlined fontSize="small" />
              Ambiente protegido
            </Typography>
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgba(231,240,250,0.88)" }}>
              <LockOutlined fontSize="small" />
              Conexão criptografada
            </Typography>
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgba(231,240,250,0.88)" }}>
              <CheckCircleOutline fontSize="small" />
              Liberação automática após pagamento
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            p: { xs: 3, md: 5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            background:
              "linear-gradient(145deg, rgba(11,18,28,0.95) 0%, rgba(9,14,23,0.95) 100%)",
          }}
        >
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.2, mb: 2 }}>
            <Box component="img" src={logo} alt="Logo Future Trade" sx={{ width: 44, height: 44, borderRadius: "50%" }} />
            <Box>
              <Typography sx={{ fontWeight: 900, color: "#F5FAFF", lineHeight: 1.1 }}>
                Future Trade
              </Typography>
              <Typography sx={{ color: "rgba(231,240,250,0.66)", fontSize: "0.8rem" }}>
                Acesso seguro ao painel
              </Typography>
            </Box>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, color: "#F5FAFF", mb: 0.8 }}>
            Entrar na conta
          </Typography>
          <Typography sx={{ color: "rgba(231,240,250,0.72)", mb: 2.2 }}>
            Use seu e-mail e senha para continuar.
          </Typography>

          <form onSubmit={handleLogin}>
            <TextField
              label="E-mail"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              InputLabelProps={{ sx: { color: "rgba(231,240,250,0.68)" } }}
              InputProps={{
                sx: {
                  color: "white",
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
              }}
            />

            <TextField
              label="Senha"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputLabelProps={{ sx: { color: "rgba(231,240,250,0.68)" } }}
              inputProps={{ style: { color: "white" } }}
              InputProps={{
                sx: {
                  color: "white",
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: showPassword ? "#9CC8FF" : "rgba(231,240,250,0.6)" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              sx={{
                mt: 2,
                py: 1.3,
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 2.5,
                background: "linear-gradient(90deg, #2E78E6 0%, #4E9AFD 100%)",
                "&:hover": { background: "linear-gradient(90deg, #3A85F0 0%, #62A8FF 100%)" },
              }}
              fullWidth
            >
              Acessar painel
            </Button>
          </form>

          <Box textAlign="center" sx={{ mt: 3 }}>
            <Link href="/esqueci-senha" underline="hover" sx={{ color: "rgba(231,240,250,0.7)" }}>
              Esqueci minha senha
            </Link>
          </Box>

          <Box textAlign="center" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: "rgba(231,240,250,0.72)" }}>
              Não tem uma conta?
            </Typography>
            <Link href="/register" underline="hover" sx={{ fontWeight: "bold", color: "#9CC8FF" }}>
              Criar minha conta
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default Login;
